import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { PlusCircle, Pencil, Trash, Check, Share2, X , Send, Info } from 'lucide-react';
import { apiCall } from '../components/ApiCalls';
import { useAuth } from '../components/AuthContext';
import { useUserId } from '../components/UserIdHook';
import { useIndexedDB } from './../hooks/useIndexedDB';
import './GroceryListPageC.scss';

const LazyGroceryList = lazy(() => import('./LazyGroceryList'));
const LazySuggestions = lazy(() => import('./LazySuggestions'));

interface GroceryItem {
  category_item_id: string;
  item_name: string;
  quantity: string;
  user_id: string;
  category: string;
}

const categories = [
  'Dairy', 'Meat', 'Vegetables', 'Fruits', 'Grains', 'Lentils and Pulses', 'Rice', 'Seafood', 'Baking', 
  'Spices', 'Pantry Staples', 'Beverages', 'Frozen Foods', 'Condiments', 'Snacks', 'Cleaning Supplies', 
  'Personal Care', 'Paper Goods', 'Sauces', 'Herbs'
];

const GroceryListPage: React.FC = () => {
  const [items, setItems] = useState<GroceryItem[]>([]);
  const [newItem, setNewItem] = useState<Omit<GroceryItem, 'category_item_id' | 'user_id'>>({
    item_name: '',
    quantity: '',
    category: categories[0],
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { user } = useAuth();
  const user_id = useUserId(user) ?? '';
  const { getItem, setItem } = useIndexedDB('groceryApp', 'groceryItems');

  const [phoneNumber, setPhoneNumber] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const startEditing = useCallback((category_item_id: string) => {
    const itemToEdit = items.find(item => item.category_item_id === category_item_id);
    if (itemToEdit) {
      setNewItem({
        item_name: itemToEdit.item_name,
        quantity: itemToEdit.quantity,
        category: itemToEdit.category,
      });
      setEditingId(category_item_id);
    }
  }, [items]);

  const copyToClipboard = useCallback(() => {
    const groupedItems = groupItemsByCategory(items);
    let listText = "My Grocery List:\n\n";

    Object.entries(groupedItems).forEach(([category, categoryItems]) => {
      listText += `${category}:\n`;
      categoryItems.forEach(item => {
        listText += `- ${item.item_name} (${item.quantity})\n`;
      });
      listText += '\n';
    });

    navigator.clipboard.writeText(listText).then(() => {
      alert('Grocery list copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      alert('Failed to copy grocery list. Please try again.');
    });
  }, [items]);

  const fetchGroceryItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const cachedItems = await getItem(user_id);
      if (cachedItems) {
        setItems(cachedItems);
        setIsLoading(false);
      }

      const fetchPayload = {
        entity: 'GroceryItemsTable',
        action: 'query',
        pk_column: 'user_id',
        pk_value: user_id,
        limit: 100,
      };

      const response = await apiCall<typeof fetchPayload, GroceryItem[] | null>({
        url: 'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        method: 'POST',
        payload: fetchPayload,
      });

      if (response.isSuccess && response.data && Array.isArray(response.data)) {
        setItems(response.data);
        setItem(user_id, response.data);
      } else {
        setError('No items found or an error occurred.');
      }
    } catch (err) {
      setError('An error occurred while fetching grocery items.');
    } finally {
      setIsLoading(false);
    }
  }, [user_id, getItem, setItem]);

  useEffect(() => {
    if (user_id) {
      fetchGroceryItems();
    }
  }, [user_id, fetchGroceryItems]);

  const groupItemsByCategory = (items: GroceryItem[]) => {
    return items.reduce((acc: { [key: string]: GroceryItem[] }, item) => {
      const category = item.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});
  };

  const groupedItems = useMemo(() => groupItemsByCategory(items), [items]);

  const addItem = useCallback(async (newItemToAdd: Omit<GroceryItem, 'category_item_id' | 'user_id'>) => {
    const newItemWithId: GroceryItem = {
      ...newItemToAdd,
      category_item_id: `${user_id}-${Date.now()}`,
      user_id: user_id
    };

    const updatedItems = [...items, newItemWithId];
    setItems(updatedItems);
    setItem(user_id, updatedItems);

    const addPayload = {
      entity: 'GroceryItemsTable',
      action: 'upsert',
      pk_column: 'user_id',
      pk_value: user_id,
      sk_column: 'category_item_id',
      sk_value: newItemWithId.category_item_id,
      columns: {
        item_name: newItemWithId.item_name,
        quantity: newItemWithId.quantity,
        category: newItemWithId.category,
      },
    };

    try {
      await apiCall({
        url: 'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        method: 'POST',
        payload: addPayload,
      });
    } catch (err) {
      console.error('An error occurred while adding the item to the database:', err);
      setItems(items); // Revert on error
      setItem(user_id, items);
    }
  }, [items, user_id, setItem]);

  const deleteItem = useCallback(async (category_item_id: string) => {
    const updatedItems = items.filter((item) => item.category_item_id !== category_item_id);
    setItems(updatedItems);
    setItem(user_id, updatedItems);

    const deletePayload = {
      entity: 'GroceryItemsTable',
      action: 'delete',
      pk_column: 'user_id',
      pk_value: user_id,
      sk_column: 'category_item_id',
      sk_value: category_item_id,
    };

    try {
      const response = await apiCall({
        url: 'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        method: 'POST',
        payload: deletePayload,
      });
  
      if (response.isSuccess) {
        console.log(`Item  deleted successfully.`);
        setItem(user_id, updatedItems); // Update local storage
      } else {
        throw new Error(response.message || 'Unknown error occurred');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      console.log('Delete payload:', JSON.stringify(deletePayload));
  
      // Revert the optimistic update
      setItems(items);
      setItem(user_id, items);
  
      if (err instanceof Error) {
        setError(`Failed to delete`);
      } else {
        setError('An unexpected error occurred while deleting ');
      }
      console.log('Delete payload:', JSON.stringify(deletePayload));
      // Show an alert for immediate user feedback
      alert(`Error: Failed to delete  Please try again.`);
    }
  }, [items, user_id, setItem]);

  const saveEdit = useCallback(async () => {
    if (editingId) {
      const updatedItems = items.map((item) =>
        item.category_item_id === editingId
          ? { ...item, ...newItem, user_id, category_item_id: editingId }
          : item
      );
      setItems(updatedItems);
      setItem(user_id, updatedItems);

      const editPayload = {
        entity: 'GroceryItemsTable',
        action: 'upsert',
        pk_column: 'user_id',
        pk_value: user_id,
        sk_column: 'category_item_id',
        sk_value: editingId,
        columns: {
          item_name: newItem.item_name,
          quantity: newItem.quantity,
          category: newItem.category,
        },
      };

      try {
        await apiCall({
          url: 'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
          method: 'POST',
          payload: editPayload,
        });
      } catch (err) {
        setError('An error occurred while updating the item in the database.');
        setItems(items); // Revert on error
        setItem(user_id, items);
      }

      setEditingId(null);
      setNewItem({ item_name: '', quantity: '', category: categories[0] });
    }
  }, [items, editingId, newItem, user_id, setItem]);

  const handleItemNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewItem((prev) => ({ ...prev, item_name: value }));
    setShowSuggestions(value.trim() !== '');
  }, []);

  const selectSuggestion = useCallback((item: GroceryItem) => {
    setNewItem({
      item_name: item.item_name,
      quantity: item.quantity,
      category: item.category,
    });
    setShowSuggestions(false);
  }, []);

  const validatePhoneNumber = (number: string) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(number);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value);
    setIsPhoneValid(validatePhoneNumber(value));
  };

  const generateMessageText = () => {
    let message = "Grocery List:\n\n";
    Object.entries(groupedItems).forEach(([category, items]) => {
      message += `${category}:\n`;
      items.forEach(item => {
        message += `- ${item.item_name} (${item.quantity})\n`;
      });
      message += '\n';
    });
    if (additionalNotes.trim()) {
      message += `\nAdditional Notes:\n${additionalNotes.trim()}`;
    }
    return message;
  };

  const sendSMS = async () => {
    if (!isPhoneValid || Object.keys(groupedItems).length === 0) return;

    const message = generateMessageText();
    try {
      const response = await apiCall({
        url: 'https://your-api-gateway-url/send-sms',
        method: 'POST',
        payload: {
          phoneNumber,
          message
        }
      });
      if (response.isSuccess) {
        alert('SMS sent successfully!');
        setShowConfirmation(false);
      } else {
        throw new Error(response.message || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS. Please try again.');
    }
  };

  const sendWhatsApp = () => {
    if (!isPhoneValid || Object.keys(groupedItems).length === 0) return;

    const message = encodeURIComponent(generateMessageText());
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    setShowConfirmation(false);
  };

  const groceryListSummary = useMemo(() => {
    return Object.entries(groupedItems).map(([category, items]) => (
      `${category}: ${items.length} items`
    )).join(', ');
  }, [groupedItems]);


  return (
    <div className="grocery-list-page">
      <div className="grocery-input-section">
        <h1 className="grocery-list-title">Add/Edit Item</h1>

        <div className="add-item-form">
          <div className="input-row">
            <div className="autosuggest-container">
              <input
                type="text"
                value={newItem.item_name}
                onChange={handleItemNameChange}
                placeholder="Add new item"
                className="add-input"
              />
              {showSuggestions && !editingId && (
                <Suspense fallback={<div>Loading suggestions...</div>}>
                  <LazySuggestions
                    inputValue={newItem.item_name}
                    onSelect={selectSuggestion}
                  />
                </Suspense>
              )}
            </div>

            <input
              type="text"
              value={newItem.quantity}
              onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
              placeholder="Qty (e.g., 2 lb)"
              className="add-input quantity"
            />
            <select
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              className="add-input"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="button-row">
            {editingId ? (
              <>
                <button onClick={saveEdit} className="save-button">
                  <Check size={18} />
                  Save
                </button>
                <button onClick={() => {
                  setEditingId(null);
                  setNewItem({ item_name: '', quantity: '', category: categories[0] });
                }} className="cancel-button">
                  <X size={18} />
                  Cancel
                </button>
              </>
            ) : (
              <button 
              onClick={() => {
                addItem(newItem);
                setNewItem({ item_name: '', quantity: '', category: categories[0] });
              }} 
              className="add-button"
              disabled={!newItem.item_name.trim()}
            >
              <PlusCircle size={18} />
              Add
            </button>
            )}
          </div>
        </div>
        <div className="send-list-section">
        <h2>Send Grocery List</h2>
       
        <div className="grocery-list-summary">
          <span>{groceryListSummary}</span>
          <div className="tooltip">
            <Info size={18} />
            <span className="tooltiptext">{generateMessageText()}</span>
          </div>
        </div>
        <textarea
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Add any additional notes for the store..."
          className="additional-notes"
        />
        <div className="send-buttons">
          <button
            onClick={() => setShowConfirmation(true)}
            disabled={!isPhoneValid || Object.keys(groupedItems).length === 0}
            className="send-button sms"
          >
            <Send size={18} />
            Send To Store
          </button>
         
        </div>
      </div>

      {showConfirmation && (
        <div className="confirmation-modal">
          <div className="modal-content">
            <h3>Confirm Sending</h3>
            <p>Are you sure you want to send the grocery list to {phoneNumber}?</p>
            <div className="modal-buttons">
              <button onClick={sendSMS} className="confirm-button">Send To Store</button>
              <button onClick={() => setShowConfirmation(false)} className="cancel-button">Cancel</button>
              </div>
          </div>
        </div>
      )}
             </div>

          

      <div className="grocery-list-section">
        <h1 className="grocery-list-title">Grocery List</h1>
        <button onClick={copyToClipboard} className="share-button">
          <Share2 size={18} />
          Share List
        </button>
        <Suspense fallback={<div>Loading grocery list...</div>}>
          <LazyGroceryList
            groupedItems={groupedItems}
            onEdit={startEditing}
            onDelete={deleteItem}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default GroceryListPage;